// useSyncLayerOrdering.ts - Custom hook to sync layer ordering changes back to settings

import { useEffect, useRef } from 'react';
import { useLayerOrderingSettings } from './useLayerOrdering';
import { LayerItem, useLayers } from '@ais-ui/react-openlayers';
import { Settings } from '@ais-ui/websockets';
import { EnabledWMSLayer } from '../../../WMSLayers/useWMSSettings';
import { isLayerFavorite } from './layerFavoriteCore';
import { useSettings } from '../../../Settings/SettingsProvider';

const isFavoriteLayer = (settings: Settings, layer: LayerItem): boolean => {
  const isVesselsLayer = layer.name.toLowerCase() === 'vessels';
  if (isVesselsLayer) return true;

  return isLayerFavorite(settings, layer.id);
};

const getEnabledWmsLayersFromSettings = (settings: Settings) => {
  const wmsInfos = settings.wmsSettings || [];
  const enabled: EnabledWMSLayer[] = [];

  wmsInfos.forEach((wmsInfo: any) => {
    if (wmsInfo.layers) {
      wmsInfo.layers.forEach((layer: any) => {
        if (layer.enabled) {
          enabled.push({ wmsInfo, wmsLayerInfo: layer });
        }
      });
    }
  });

  return enabled;
};

// Helper to safely get layer type for settings
const getLayerTypeForSettings = (
  layer: LayerItem,
  settings: Settings,
): string | undefined => {
  // Be very careful with type mapping to avoid corruption
  if (layer.name.toLowerCase() === 'vessels') return 'vessels';

  // Check layer type first
  switch (layer.type) {
    case 'linestring':
    case 'polygon':
    case 'point':
    case 'circle':
      return 'shape';
    case 'wms':
      return 'wms';
    case 'coverage':
    case 'source-identifiers':
      return 'coverage';
    case 'gps-lost-grid':
      return 'gps-lost';
    case 'openseamap':
      return 'openseamap';
    case 'vectortile':
      return 'vectortile';
    case 'abb-weather':
      return 'abb-weather';
    case 'cambridge-radar':
      return 'cambridge-radar';
  }

  // More robust WMS detection
  const source =
    'getSource' in layer.layer
      ? (layer.layer as { getSource: () => unknown }).getSource()
      : undefined;
  if (source && typeof source === 'object') {
    // Check for TileWMS source methods/properties instead of constructor name
    const src = source as Record<string, unknown>;
    if (src.getUrls || src.getUrl || src.getParams) {
      return 'wms';
    }

    // Check source class string representation
    const sourceString = source.toString();
    if (sourceString.includes('TileWMS') || sourceString.includes('ImageWMS')) {
      return 'wms';
    }
  }

  // Check if layer ID matches known WMS layers from settings
  // This is a fallback that should be reliable
  const enabledWmsLayers = getEnabledWmsLayersFromSettings(settings);
  if (enabledWmsLayers.some((wms) => wms.wmsLayerInfo.id === layer.id)) {
    return 'wms';
  }

  return undefined;
};

// Helper to check if layer has a valid type for settings
const hasValidTypeForSettings = (
  layer: LayerItem,
  settings: Settings,
): boolean => {
  return getLayerTypeForSettings(layer, settings) !== undefined;
};

// Custom hook to sync layer ordering changes back to settings
export const useSyncLayerOrdering = () => {
  const { layers } = useLayers();
  const { saveLayerOrderingToSettings, getExistingLayerOrdering } =
    useLayerOrderingSettings();
  const { settings } = useSettings();

  // Keep track of the previous layer order to detect changes
  const previousLayerOrderRef = useRef<string>('');
  const syncTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Always keep the latest functions/values in refs so debounced callbacks use fresh data
  const getExistingLayerOrderingRef = useRef(getExistingLayerOrdering);
  getExistingLayerOrderingRef.current = getExistingLayerOrdering;
  const saveRef = useRef(saveLayerOrderingToSettings);
  saveRef.current = saveLayerOrderingToSettings;

  useEffect(() => {
    // Skip if no layers yet or during initial load
    if (layers.length === 0) {
      return;
    }

    // Filter layers that are both favorite AND have valid types
    const validLayers = layers.filter(
      (layer) =>
        isFavoriteLayer(settings, layer) &&
        hasValidTypeForSettings(layer, settings),
    );

    // Create a simple string representation of current layer order for comparison
    const currentLayerOrder = validLayers
      .map(
        (layer) =>
          `${layer.id}:${layer.isVisible}:${getLayerTypeForSettings(
            layer,
            settings,
          )}`,
      )
      .join('|');

    // Skip if no change detected
    if (previousLayerOrderRef.current === currentLayerOrder) {
      return;
    }

    // Skip if this is the first time (no previous order to compare)
    if (previousLayerOrderRef.current === '') {
      previousLayerOrderRef.current = currentLayerOrder;
      return;
    }

    // Debounce the sync to avoid too many saves during rapid changes
    if (syncTimeoutRef.current) {
      clearTimeout(syncTimeoutRef.current);
    }

    syncTimeoutRef.current = setTimeout(() => {
      try {
        // Create new ordering based on current ON-MAP layer state
        const onMapOrdering = validLayers
          .map((layer) => {
            const type = getLayerTypeForSettings(layer, settings);
            // Double-check type is defined (TypeScript guard)
            if (!type) return null;

            return {
              type,
              // Use stable ID for vessels since it gets a new UUID each session
              id: type === 'vessels' ? 'ships-layer' : layer.id,
              enabled: layer.isVisible,
            };
          })
          .filter((item): item is NonNullable<typeof item> => item !== null);

        // Read existing ordering via ref to get the LATEST data (not stale closure).
        // This prevents a race where toggleFavorite saves a radar entry to ordering
        // but the debounced timeout was captured with a stale getExistingLayerOrdering.
        const existingOrdering = getExistingLayerOrderingRef.current() || [];
        const onMapIds = new Set(onMapOrdering.map((l) => l.id));
        const offMapFavorites = existingOrdering.filter(
          (entry) => !onMapIds.has(entry.id) && entry.type !== 'vessels',
        );

        // Preserve off-map favorites' position relative to ships-layer.
        // Appending all at the end would move "below-ships" entries to
        // "above-ships" on next reload (determineLayerPosition checks index).
        const shipsIdx = onMapOrdering.findIndex((l) => l.id === 'ships-layer');
        const existingShipsIdx = existingOrdering.findIndex(
          (e) => e.type === 'vessels',
        );

        let newOrdering;
        if (shipsIdx === -1 || existingShipsIdx === -1) {
          newOrdering = [...onMapOrdering, ...offMapFavorites];
        } else {
          const beforeShips = offMapFavorites.filter((f) => {
            const origIdx = existingOrdering.findIndex((e) => e.id === f.id);
            return origIdx !== -1 && origIdx < existingShipsIdx;
          });
          const afterShips = offMapFavorites.filter((f) => {
            const origIdx = existingOrdering.findIndex((e) => e.id === f.id);
            return origIdx === -1 || origIdx > existingShipsIdx;
          });
          newOrdering = [
            ...onMapOrdering.slice(0, shipsIdx),
            ...beforeShips,
            ...onMapOrdering.slice(shipsIdx),
            ...afterShips,
          ];
        }

        // Only save if we have a meaningful ordering
        if (newOrdering.length > 0) {
          saveRef.current(newOrdering);
        }

        previousLayerOrderRef.current = currentLayerOrder;
      } catch (error) {
        console.error('Error syncing layer ordering:', error);
      }
    }, 1000);

    // Cleanup timeout on unmount or change
    return () => {
      if (syncTimeoutRef.current) {
        clearTimeout(syncTimeoutRef.current);
      }
    };
  }, [layers, saveLayerOrderingToSettings, getExistingLayerOrdering, settings]);
};
